import React, { useEffect } from 'react';

import { FormElement, Label } from '@dashboard/components/Form';
import { Select } from '@dashboard/components/Formik';
import FormError from '@dashboard/components/Formik/components/FormError';

import {
  SELECTION_MODES,
  boundsForMode,
  fitSelections,
  countChoices,
  elementForMode,
} from '../../selectionRules';

// A default picked under one rule rarely fits another (three ticks under "Up
// to 2", a single pick under "Any number"), so any change to the mode or a
// count clears the whole default. Cleared on change rather than on save so a
// default picked afterwards in the same edit survives.
export const CLEARED_DEFAULT_VALUE = { enabled: false, value: '', values: [] };

export default function SelectionsField({ fieldKey, values, setFieldValue }) {
  const optionCount = (values.options ?? []).filter(option => option.label).length;
  const mode = values.selectionMode ?? 'one';
  const other = position => (position === 'low' ? values.maxValue?.value : values.minValue?.value);
  const clearDefault = () => setFieldValue('defaultValue', CLEARED_DEFAULT_VALUE);

  useEffect(() => {
    const fitted = fitSelections(mode, values.minValue, values.maxValue, optionCount);
    if (!fitted) return;
    setFieldValue('minValue', fitted.minValue);
    setFieldValue('maxValue', fitted.maxValue);
  }, [mode, optionCount, values.minValue?.value, values.maxValue?.value]);
  const count = (name, position) => (
    <Select
      id={`${fieldKey}_${name}`}
      name={name}
      options={countChoices(mode, position, optionCount, other(position))}
      className="min-w-0 flex-1"
      onChange={clearDefault}
    />
  );

  return (
    <FormElement>
      <Label for={`${fieldKey}_selectionMode`}>Selections allowed</Label>
      <div className="grid grid-cols-2 items-center gap-2">
        <Select
          id={`${fieldKey}_selectionMode`}
          name="selectionMode"
          options={SELECTION_MODES}
          onChange={e => {
            const nextMode = e.target.value;
            const next = boundsForMode(nextMode, values.minValue, values.maxValue, optionCount);
            setFieldValue('element', elementForMode(nextMode, values.layout));
            setFieldValue('minValue', next.minValue);
            setFieldValue('maxValue', next.maxValue);
            clearDefault();
          }}
        />
        <div className="flex items-center gap-2">
          {(mode === 'atleast' || mode === 'exactly') && count('minValue.value', 'low')}
          {mode === 'upto' && count('maxValue.value', 'high')}
          {mode === 'between' && (
            <>
              {count('minValue.value', 'low')}
              <span className="text-sm text-gray-700 dark:text-muted-foreground">and</span>
              {count('maxValue.value', 'high')}
            </>
          )}
        </div>
      </div>
      <FormError name="minValue.value" />
      <FormError name="maxValue.value" />
    </FormElement>
  );
}
